import React from 'react'
import { AircraftOverride, AircraftTransaction, TransactionOverride, MarketCustomColumn } from '../../../types/database'
import { getEffectiveCustomColumnValue } from '../../../utils/overrides'
import InlineCustomColumnEditor from '../../inline-editors/InlineCustomColumnEditor'
import { CUSTOM_COLUMN_MAX_CHARS } from '../utils'
import { CellBaseProps, resolveOverrideContext, isFieldEditing, makeEditClickHandler, makeFieldSaveHandler, overrideEditableClass } from './types'
import type { EditingCellFieldName } from '../types'

interface CustomColumnsCellProps extends CellBaseProps {
  customColumns: MarketCustomColumn[]
}

/**
 * Renders all custom column cells for a given row. Returns an array of <td> elements,
 * one per custom column.
 */
export function CustomColumnsCells(props: CustomColumnsCellProps) {
  const { aircraft, cellBorderClass, customColumns, aircraftTransaction } = props
  const { isSoldWithTransaction, isEditable, transOverride, override } = resolveOverrideContext(props)

  return (
    <>
      {customColumns.map((column) => {
        const customFieldName = `custom_${column.column_index}_value` as EditingCellFieldName

        const customValue = isSoldWithTransaction
          ? (transOverride?.[customFieldName as keyof TransactionOverride] as string ?? aircraftTransaction![customFieldName as keyof AircraftTransaction] as string ?? '-')
          : (getEffectiveCustomColumnValue(column.column_index, aircraft, override) || '-')
        const needsWrap = customValue.length > CUSTOM_COLUMN_MAX_CHARS

        const hasOverride = isSoldWithTransaction
          ? (transOverride?.[customFieldName as keyof TransactionOverride] != null)
          : (override?.[`custom_${column.column_index}_value` as keyof AircraftOverride] != null)

        const isEditing = isFieldEditing(props, customFieldName, isSoldWithTransaction)

        const currentEditorValue = isSoldWithTransaction
          ? (transOverride?.[customFieldName as keyof TransactionOverride] as string | null)
          : (override?.[`custom_${column.column_index}_value` as keyof AircraftOverride] as string | null)

        return (
          <td key={column.id} className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass} relative`}>
            <div
              className={`${overrideEditableClass(hasOverride, isEditable)} ${needsWrap ? "break-words whitespace-normal" : "whitespace-nowrap"}`}
              style={needsWrap ? { maxWidth: `${CUSTOM_COLUMN_MAX_CHARS}ch`, margin: '0 auto' } : undefined}
              onClick={makeEditClickHandler(props, customFieldName, isEditable, isSoldWithTransaction)}
            >
              {isEditing ? (
                <InlineCustomColumnEditor
                  value={currentEditorValue}
                  customColumn={column}
                  onSave={makeFieldSaveHandler(props, customFieldName, isSoldWithTransaction)}
                  onCancel={props.handleCancelEdit}
                />
              ) : (
                customValue
              )}
            </div>
          </td>
        )
      })}
    </>
  )
}

